/**
 * Version Checker Utility
 * 
 * This utility checks for new application versions and can prompt users to refresh.
 * Helps ensure users are always running the latest version after deployments.
 * 
 * Security Consideration:
 * - Uses fetch with proper error handling to prevent information leakage
 * - No sensitive data is exposed in version checks
 */

interface VersionInfo {
  version: string;
  gitCommit: string;
  buildTime: string;
  appVersion: string;
}

class VersionChecker {
  private currentVersion: string | null = null;
  private checkInterval: number = 5 * 60 * 1000; // 5 minutes
  private intervalId: NodeJS.Timeout | null = null;

  constructor() {
    this.currentVersion = this.getCurrentVersion();
  }

  /**
   * Get current version from meta tag
   */
  private getCurrentVersion(): string | null {
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag?.getAttribute('content') || null;
  }

  /**
   * Fetch latest version from server
   */
  private async fetchLatestVersion(): Promise<VersionInfo | null> {
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Version check failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn('Version check error:', error);
      return null;
    }
  }

  /**
   * Check if a new version is available
   */
  async checkForUpdates(): Promise<boolean> {
    if (!this.currentVersion) {
      console.warn('Current version not found');
      return false;
    }

    const latestVersion = await this.fetchLatestVersion();

    if (!latestVersion) {
      return false;
    }

    // Compare versions
    const hasUpdate = latestVersion.appVersion !== this.currentVersion;

    if (hasUpdate) {
      console.log('New version available:', {
        current: this.currentVersion,
        latest: latestVersion.appVersion,
      });
    }

    return hasUpdate;
  }

  /**
   * Start periodic version checking
   */
  startPeriodicCheck(callback?: (hasUpdate: boolean) => void) {
    // Check immediately
    this.checkForUpdates().then((hasUpdate) => {
      if (hasUpdate && callback) {
        callback(hasUpdate);
      }
    });

    // Then check periodically
    this.intervalId = setInterval(async () => {
      const hasUpdate = await this.checkForUpdates();
      if (hasUpdate && callback) {
        callback(hasUpdate);
      }
    }, this.checkInterval);
  }

  /**
   * Stop periodic version checking
   */
  stopPeriodicCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Force reload the application (clears cache)
   */
  forceReload() {
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    
    // Hard reload
    window.location.reload();
  }

  /**
   * Show update notification (optional - can be customized)
   */
  showUpdateNotification(onUpdate: () => void) {
    // Simple browser notification approach
    // In production, you might want to use a toast/modal component instead
    const shouldUpdate = confirm(
      'A new version of the application is available. Would you like to refresh to get the latest updates?'
    );

    if (shouldUpdate) {
      onUpdate();
    }
  }
}

// Create singleton instance
export const versionChecker = new VersionChecker();

// Export for advanced usage
export default VersionChecker;

/**
 * Usage Examples:
 * 
 * 1. Basic version check:
 * ```
 * import { versionChecker } from '@/utils/versionChecker';
 * 
 * const hasUpdate = await versionChecker.checkForUpdates();
 * if (hasUpdate) {
 *   versionChecker.forceReload();
 * }
 * ```
 * 
 * 2. Periodic checking with notification:
 * ```
 * versionChecker.startPeriodicCheck((hasUpdate) => {
 *   if (hasUpdate) {
 *     versionChecker.showUpdateNotification(() => {
 *       versionChecker.forceReload();
 *     });
 *   }
 * });
 * ```
 * 
 * 3. In React app (App.tsx):
 * ```
 * useEffect(() => {
 *   versionChecker.startPeriodicCheck((hasUpdate) => {
 *     if (hasUpdate) {
 *       // Show your custom notification component
 *       setShowUpdateBanner(true);
 *     }
 *   });
 * 
 *   return () => versionChecker.stopPeriodicCheck();
 * }, []);
 * ```
 */
